import { AnimationEvent, Portal, usePopup } from '@libs/core';
import { TPopupProps } from '@libs/types';
import clsx from 'clsx';
import React, { TouchEvent, useCallback, useEffect, useEffectEvent, useState } from 'react';
import { flushSync } from 'react-dom';
import OfflinePopup from '../OfflinePopup';

import styles from './bottomSheet.module.css';

const Popup = ({ content, modal, setPopup }: TPopupProps) => {
    const [animated, setAnimated] = useState(false);
    const [data, setData] = useState<typeof content>(null);
    const { handlers, overlayRef, overlayClick } = usePopup(data, setAnimated, modal);

    const updatePopup = useEffectEvent((content: TPopupProps['content'], data: TPopupProps['content']) => {
        if (content) {
            setData(content);
        } else if (data) {
            setAnimated(true);
        }
    });

    useEffect(() => {
        updatePopup(content, data);
    }, [content, data]);

    const closeAnimationPopup = useCallback(
        (e: AnimationEvent<HTMLDivElement>) => {
            if (e.target !== e.currentTarget) return;
            // The opening animation ends here too — only the closing one unmounts the popup
            if (!animated) return;
            flushSync(() => {
                setData(null);
                setAnimated(false);
                setPopup(null);
            });
        },
        [animated, setPopup],
    );

    const touchEnd = (e: TouchEvent) => {
        const target = e?.target as HTMLDivElement;
        const dashWrapper = target?.getAttribute?.('dash');
        if (dashWrapper) {
            e.preventDefault();
            setAnimated(true);
        }
    };

    if (data) {
        const isOffline = React.isValidElement(data) && (data.type === OfflinePopup || (typeof data.type === 'function' && data.type.name === 'OfflinePopup'));

        return (
            <Portal>
                <div id="popup" {...handlers}>
                    <div
                        className={clsx(styles.overlay, animated && styles.overlayClosing)}
                        onClick={overlayClick}
                        style={isOffline ? { zIndex: 'calc(var(--zindex-loader-layout) + 1)' } : undefined}
                    >
                        <div className={styles.popupOuter}>
                            <div
                                className={clsx(styles.popupBox, animated && styles.closeAnimationName)}
                                onAnimationEnd={closeAnimationPopup}
                                ref={overlayRef}
                                onTouchEnd={touchEnd}
                            >
                                {data}
                            </div>
                        </div>
                    </div>
                </div>
            </Portal>
        );
    }
    return null;
};
export default Popup;
